/**
 * 六边形几何计算工具类
 * 处理坐标转换、邻居判定、边界检测等核心几何逻辑
 */
import { Vector2, HexCoord, EdgeDirection, EdgeData } from '../types';

export class HexMath {
    // 六边形的6个方向偏移量（对应 EdgeDirection 枚举）
    private static readonly NEIGHBOR_OFFSETS: ReadonlyArray<HexCoord> = [
        { u: -2, v: 0 },   // Left
        { u: -1, v: 2 },   // TopLeft
        { u: 1, v: 2 },    // TopRight
        { u: 2, v: 0 },    // Right
        { u: 1, v: -2 },   // BottomRight
        { u: -1, v: -2 }   // BottomLeft
    ];

    // 60度的弧度值
    public static readonly DEG_60 = Math.PI / 3;

    // sqrt(0.75) 常量，用于六边形高度计算
    public static readonly SQRT_075 = Math.sqrt(0.75);

    /**
     * 检查坐标是否为有效的六边形中心点
     * 规则：(u + v/2) 必须是偶数
     */
    static isValidHexCoord(coord: HexCoord): boolean {
        return Math.abs(coord.u + coord.v / 2) % 2 === 0;
    }

    /**
     * 获取六边形的所有邻居坐标
     */
    static getNeighbors(coord: HexCoord): HexCoord[] {
        return this.NEIGHBOR_OFFSETS.map(offset => ({
            u: coord.u + offset.u,
            v: coord.v + offset.v
        }));
    }

    /**
     * 获取指定方向的邻居坐标
     */
    static getNeighbor(coord: HexCoord, direction: EdgeDirection): HexCoord {
        const offset = this.NEIGHBOR_OFFSETS[direction];
        return {
            u: coord.u + offset.u,
            v: coord.v + offset.v
        };
    }

    /**
     * 判断两个六边形是否相邻
     */
    static areNeighbors(coord1: HexCoord, coord2: HexCoord): boolean {
        const du = coord2.u - coord1.u;
        const dv = coord2.v - coord1.v;

        return this.NEIGHBOR_OFFSETS.some(
            offset => offset.u === du && offset.v === dv
        );
    }

    /**
     * 获取两个相邻六边形之间的共享边方向
     * 返回从 coord1 看向 coord2 的方向
     */
    static getSharedEdgeDirection(coord1: HexCoord, coord2: HexCoord): EdgeDirection | null {
        const du = coord2.u - coord1.u;
        const dv = coord2.v - coord1.v;

        const index = this.NEIGHBOR_OFFSETS.findIndex(
            offset => offset.u === du && offset.v === dv
        );

        return index >= 0 ? index as EdgeDirection : null;
    }

    /**
     * 获取相反方向
     */
    static getOppositeDirection(direction: EdgeDirection): EdgeDirection {
        return ((direction + 3) % 6) as EdgeDirection;
    }

    /**
     * 获取六边形某条边的唯一标识 Key
     * 确保相邻两个六边形的共享边具有相同的 Key
     */
    static getEdgeKey(coord: HexCoord, direction: EdgeDirection): string {
        const { u, v } = coord;

        switch (direction) {
            case EdgeDirection.Left:
                return `v_${u - 1}_${v - 1}`;
            case EdgeDirection.TopLeft:
                return `h_${u - 1}_${v + 1}`;
            case EdgeDirection.TopRight:
                return `h_${u}_${v + 1}`;
            case EdgeDirection.Right:
                return `v_${u + 1}_${v - 1}`;
            case EdgeDirection.BottomRight:
                return `h_${u}_${v - 1}`;
            case EdgeDirection.BottomLeft:
                return `h_${u - 1}_${v - 1}`;
            default:
                throw new Error(`Invalid direction: ${direction}`);
        }
    }

    /**
     * 获取六边形所有边的数据
     */
    static getPieceEdges(coord: HexCoord): EdgeData[] {
        const { u, v } = coord;

        return [
            { key: `v_${u - 1}_${v - 1}`, type: 'v', x: u - 1, y: v - 1, reverse: false },  // Left
            { key: `h_${u - 1}_${v + 1}`, type: 'h', x: u - 1, y: v + 1, reverse: false },  // TopLeft
            { key: `h_${u}_${v + 1}`, type: 'h', x: u, y: v + 1, reverse: false },  // TopRight
            { key: `v_${u + 1}_${v - 1}`, type: 'v', x: u + 1, y: v - 1, reverse: true },   // Right
            { key: `h_${u}_${v - 1}`, type: 'h', x: u, y: v - 1, reverse: true },   // BottomRight
            { key: `h_${u - 1}_${v - 1}`, type: 'h', x: u - 1, y: v - 1, reverse: true }    // BottomLeft
        ];
    }

    /**
     * 逻辑坐标转换为屏幕坐标（未缩放）
     */
    static hexToPixel(coord: HexCoord, scale: number = 1): Vector2 {
        return {
            x: coord.u * scale,
            y: coord.v * scale * this.SQRT_075
        };
    }

    /**
     * 屏幕坐标转换为最近的六边形坐标
     */
    static pixelToHex(pixel: Vector2, scale: number = 1): HexCoord {
        const u = pixel.x / scale;
        const v = pixel.y / (scale * this.SQRT_075);

        // 四舍五入到最近的有效坐标
        let roundU = Math.round(u);
        let roundV = Math.round(v / 2) * 2;  // v 必须是偶数

        // 确保是有效的六边形坐标
        if (!this.isValidHexCoord({ u: roundU, v: roundV })) {
            // 尝试相邻的 u 值
            roundU = Math.round(u) + (u > Math.round(u) ? 1 : -1);
        }

        return { u: roundU, v: roundV };
    }

    /**
     * 计算两个坐标之间的距离（六边形网格距离）
     */
    static distance(coord1: HexCoord, coord2: HexCoord): number {
        const du = Math.abs(coord2.u - coord1.u);
        const dv = Math.abs(coord2.v - coord1.v);
        return Math.max(du, dv / 2, (du + dv / 2) / 2);
    }

    /**
     * 生成指定层数的所有六边形坐标
     */
    static generateHexGrid(rings: number): HexCoord[] {
        const coords: HexCoord[] = [];
        const range = 2 * rings + 2;

        for (let v = -range; v <= range; v += 2) {
            for (let u = -range; u <= range; u++) {
                if (this.isValidHexCoord({ u, v })) {
                    coords.push({ u, v });
                }
            }
        }

        return coords;
    }

    /**
     * 旋转方向（60度步进）
     * steps: 正数顺时针，负数逆时针
     */
    static rotateDirection(direction: EdgeDirection, steps: number): EdgeDirection {
        return (((direction + steps) % 6) + 6) % 6 as EdgeDirection;
    }

    /**
     * 将角度标准化到 [0, 360) 范围
     */
    static normalizeAngle(angle: number): number {
        return ((angle % 360) + 360) % 360;
    }

    /**
     * 将角度对齐到 60 度步进
     */
    static snapAngleTo60(angle: number): number {
        const normalized = this.normalizeAngle(angle);
        return Math.round(normalized / 60) * 60 % 360;
    }

    /**
     * 向量相加
     */
    static add(v1: Vector2, v2: Vector2): Vector2 {
        return { x: v1.x + v2.x, y: v1.y + v2.y };
    }

    /**
     * 向量相减
     */
    static sub(v1: Vector2, v2: Vector2): Vector2 {
        return { x: v1.x - v2.x, y: v1.y - v2.y };
    }

    /**
     * 向量数乘
     */
    static mul(scalar: number, v: Vector2): Vector2 {
        return { x: scalar * v.x, y: scalar * v.y };
    }

    /**
     * 向量旋转90度
     */
    static rot90(v: Vector2): Vector2 {
        return { x: -v.y, y: v.x };
    }

    /**
     * 向量旋转任意角度
     */
    static rotate(v: Vector2, angleDeg: number): Vector2 {
        const rad = angleDeg * Math.PI / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        return {
            x: v.x * cos - v.y * sin,
            y: v.x * sin + v.y * cos
        };
    }

    /**
     * 计算向量长度
     */
    static vectorLength(v: Vector2): number {
        return Math.sqrt(v.x * v.x + v.y * v.y);
    }

    /**
     * 计算两点距离
     */
    static distancePoints(p1: Vector2, p2: Vector2): number {
        return this.vectorLength(this.sub(p2, p1));
    }
}
